import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaChartBar, FaShieldAlt, FaBriefcase, FaChevronDown, FaArrowRight } from 'react-icons/fa';
import img1 from "../../Assets/faq/faq1.png";
import img2 from "../../Assets/faq/pis.png";
import img3 from "../../Assets/faq/fees.png";

import './Faq.css';

const categories = [
    { id: 'general',  label: 'General & Tax Prep',      icon: <FaChartBar /> },
    { id: 'planning', label: 'Planning & IRS Support',  icon: <FaShieldAlt /> },
    { id: 'business', label: 'Fees & Business Services', icon: <FaBriefcase /> }
];

const faqs = {
    general: [
        {
            id: 1,
            question: "What types of tax services do you offer?",
            answer: "We offer a wide range of tax consulting services including individual and corporate tax preparation, strategic tax planning, IRS/CRA audit representation, estate and trust tax services, and proactive business advisory."
        },
        {
            id: 2,
            question: "How can I schedule a consultation?",
            answer: "You can schedule your consultation by calling our office directly at +1 (647) 855-6177, emailing us at info@orientalbiz.ca, or using our online contact form."
        },
        {
            id: 3,
            question: "What are your office hours?",
            answer: "Our general office hours are Monday through Friday, 9:00 AM to 6:00 PM. Weekend appointments and extended tax-season hours are available upon request."
        },
        {
            id: 4,
            question: "What documents do I need to bring for tax preparation?",
            answer: "Please bring government-issued photo ID, Social Security / Social Insurance numbers, W-2/T4 forms, 1099/T5 forms, records of other income, receipts for deductions (charity, medical, business expenses), and your previous year's tax return."
        },
        {
            id: 5,
            question: "How long does it take to prepare my taxes?",
            answer: "Simple individual returns can often be completed within 1 to 2 days once all documents are received. More complex corporate returns or extensive schedules may take a week or more to analyze and optimize."
        }
    ],
    planning: [
        {
            id: 6,
            question: "Can you help with both federal and provincial/state taxes?",
            answer: "Yes! We are fully experienced in preparing and optimizing federal returns as well as state/provincial tax filings across Canada and the United States."
        },
        {
            id: 7,
            question: "What is tax planning and why is it important?",
            answer: "Tax planning is the proactive process of analyzing your financial situation to minimize your future tax liabilities. It helps you keep more of your hard-earned money and ensures you are fully compliant without paying a dollar more than required."
        },
        {
            id: 8,
            question: "When should I start tax planning for the next year?",
            answer: "The best time to start planning is at the beginning of the financial year. Early planning allows you to restructure investments, set up corporate document systems, and capture credits before deadlines pass."
        },
        {
            id: 9,
            question: "What should I do if I receive a notice or letter from the CRA/IRS?",
            answer: "Do not ignore it. Notices have strict response deadlines. Contact us immediately, send over a copy of the letter, and our specialists will interpret the notice and draft an appropriate professional response."
        },
        {
            id: 10,
            question: "Can you represent me in a CRA or IRS audit?",
            answer: "Yes. As authorized tax representatives, we can represent you directly in front of tax authorities, handling the correspondence, audits, and negotiations on your behalf so you don't have to face them alone."
        }
    ],
    business: [
        {
            id: 11,
            question: "How much do your services cost?",
            answer: "Our fees are structured transparently based on the complexity of your returns and the required bookkeeping volume. We offer upfront pricing estimates during our initial consultations."
        },
        {
            id: 12,
            question: "What payment methods do you accept?",
            answer: "We accept e-Transfers, checks, major credit cards, and cash. We also offer flexible retainer structures for ongoing monthly business support."
        },
        {
            id: 13,
            question: "Do you provide accounting services for small businesses?",
            answer: "Absolutely. We specialize in end-to-end small business solutions, including monthly bookkeeping, payroll processing, tax filing, HST rebate calculations, and corporate registration."
        },
        {
            id: 14,
            question: "How can you help my business save on corporate taxes?",
            answer: "We analyze your business structures, identify eligible write-offs and tax credits, optimize depreciation claims, and structure payroll vs. dividends to achieve the lowest overall tax burden."
        },
        {
            id: 15,
            question: "How can I get in touch with you?",
            answer: "Reach out via our office phone +1 (647) 855-6177, email us at info@orientalbiz.ca, or fill out the contact form on our website to receive a prompt reply."
        }
    ]
};

const AccordionItem = ({ faq, isOpen, onToggle }) => {
    const contentRef = useRef(null);

    return (
        <div className={`faq-acc__item ${isOpen ? 'faq-acc__item--open' : ''}`}>
            <button className="faq-acc__trigger" onClick={onToggle}>
                <span className="faq-acc__question">{faq.question}</span>
                <span className="faq-acc__icon-wrap">
                    <FaChevronDown className="faq-acc__arrow" />
                </span>
            </button>
            <div 
                className="faq-acc__content" 
                ref={contentRef}
                style={{ maxHeight: isOpen ? `${contentRef.current?.scrollHeight}px` : '0px' }}
            >
                <div className="faq-acc__inner">
                    <p>{faq.answer}</p>
                </div>
            </div>
        </div>
    );
};

const FaqQuestions = () => {
    const [activeTab, setActiveTab] = useState('general');
    const [openId, setOpenId] = useState(null);
    const sectionRef = useRef(null);

    useEffect(() => {
        // Reset open accordion item on tab change
        setOpenId(null);
    }, [activeTab]);

    const handleToggle = (id) => {
        setOpenId(openId === id ? null : id);
    };

    return (
        <section className="faq-section" ref={sectionRef}>
            <div className="container-xxl">
                
                {/* ── Intro ── */}
                <div className="faq-intro">
                    <span className="faq-intro__label">QUESTIONS &amp; ANSWERS</span>
                    <h2 className="faq-intro__title">Frequently Asked Questions</h2>
                    <p className="faq-intro__sub">
                        Find quick answers to common questions about our tax consulting, planning, 
                        and business services. Can't find what you need? Reach out to our team.
                    </p>
                    <div className="faq-intro__line" />
                </div>

                {/* ── Tabs ── */}
                <div className="faq-tabs">
                    {categories.map((tab) => (
                        <button
                            key={tab.id}
                            className={`faq-tab ${activeTab === tab.id ? 'faq-tab--active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            <span className="faq-tab__icon">{tab.icon}</span>
                            <span className="faq-tab__label">{tab.label}</span>
                        </button>
                    ))}
                </div>

                {/* ── Grid Content ── */}
                <div className="faq-main-grid">
                    
                    {/* Left: Accordion list */}
                    <div className="faq-list-col">
                        <div className="faq-acc">
                            {faqs[activeTab].map((faq) => (
                                <AccordionItem
                                    key={faq.id}
                                    faq={faq}
                                    isOpen={openId === faq.id}
                                    onToggle={() => handleToggle(faq.id)}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Right: Premium CTA Card / Media */}
                    <div className="faq-cta-col">
                        <div className="faq-cta-card">
                            <div className="faq-cta-card__img-wrap">
                                <img src={activeTab === 'general' ? img1 : activeTab === 'planning' ? img2 : img3} alt="FAQ support" className="faq-cta-card__img" />
                                <div className="faq-cta-card__overlay" />
                            </div>
                            <div className="faq-cta-card__body">
                                <h3>Still Have Questions?</h3>
                                <p>
                                    Our tax experts are ready to assist you. Get custom consultations 
                                    and secure strategies for your business growth.
                                </p>
                                <Link to="/contact" className="faq-cta-card__btn">
                                    Contact Us
                                    <FaArrowRight />
                                </Link>
                            </div>
                        </div>
                    </div>

                </div>

            </div>
        </section>
    );
};

export default FaqQuestions;